/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Flex, Heading, IconButton, Label, Text } from 'theme-ui';
import { Dialog, DialogBackdrop, DialogStateReturn } from 'reakit/Dialog';
import { XIcon } from '@heroicons/react/outline';
import Button from '@/components/Button';
import { PreviewColorScheme, useWidgetPreview } from './useWidgetPreview';
import FitViewportHelp, { FIT_VIEWPORT_HELP_TEXT } from './FitViewportHelp';

type PreviewDevice = 'desktop' | 'mobile';

const MOBILE_FRAME_WIDTH = '390px';

interface Props {
    dialog: DialogStateReturn;
    configuration: Record<string, unknown> | null;
    companySitepath: string;
    embedScriptUrl: string;
    url: string;
    hasConfiguration: boolean;
    onCopyLink: () => void;
    copiedLink: boolean;
    onCopyScript: () => void;
    copiedScript: boolean;
    onCopyFullScript: () => void;
    copiedFullScript: boolean;
    fitViewport?: boolean;
    onFitViewportChange?: (checked: boolean) => void;
}

const WidgetPreviewDialog: React.FC<Props> = ({
    dialog,
    configuration,
    companySitepath,
    embedScriptUrl,
    url,
    hasConfiguration,
    onCopyLink,
    copiedLink,
    onCopyScript,
    copiedScript,
    onCopyFullScript,
    copiedFullScript,
    fitViewport,
    onFitViewportChange,
}) => {
    const [scheme, setScheme] = useState<PreviewColorScheme>('light');
    const [device, setDevice] = useState<PreviewDevice>('desktop');
    const mountWidget = useWidgetPreview(configuration, companySitepath, embedScriptUrl, scheme, fitViewport);

    return (
        <DialogBackdrop {...dialog}>
            <Dialog
                {...dialog}
                aria-label="Widget preview"
                sx={{
                    width: '90vw',
                    maxWidth: '960px',
                    '&&': {
                        padding: 4,
                        height: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                        position: 'relative',
                    },
                }}
            >
                <IconButton
                    type="button"
                    aria-label="Close"
                    onClick={dialog.hide}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    }}
                >
                    <XIcon width={18} height={18} />
                </IconButton>
                <Flex
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                        flexShrink: 0,
                    }}
                >
                    <Heading as="h3">Preview</Heading>
                    <Flex sx={{ gap: 3, flexWrap: 'wrap', alignItems: 'center', flexGrow: '1', justifyContent: 'space-between' }}>
                        <Flex sx={{ gap: 2 }}>
                            <Button variant="secondary" type="button" onClick={onCopyLink} disabled={!url}>
                                {copiedLink ? 'Copied!' : 'Copy link'}
                            </Button>
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={onCopyScript}
                                disabled={!url || !embedScriptUrl}
                            >
                                {copiedScript ? 'Copied!' : 'Copy embed script'}
                            </Button>
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={onCopyFullScript}
                                disabled={!url || !embedScriptUrl || !hasConfiguration}
                            >
                                {copiedFullScript ? 'Copied!' : 'Copy full script'}
                            </Button>
                        </Flex>
                        <Flex sx={{ gap: 2 }}>
                            <Button
                                type="button"
                                variant={device === 'desktop' ? 'primary' : 'secondary'}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </Button>
                            <Button
                                type="button"
                                variant={device === 'mobile' ? 'primary' : 'secondary'}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </Button>
                            <Button
                                type="button"
                                variant={scheme === 'light' ? 'primary' : 'secondary'}
                                onClick={() => setScheme('light')}
                            >
                                Light
                            </Button>
                            <Button
                                type="button"
                                variant={scheme === 'dark' ? 'primary' : 'secondary'}
                                onClick={() => setScheme('dark')}
                            >
                                Dark
                            </Button>
                            <Button type="button" variant="secondary" onClick={dialog.hide}>
                                Close
                            </Button>
                            <Label sx={{ width: 'auto', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(fitViewport)}
                                    onChange={(e) => onFitViewportChange?.(e.target.checked)}
                                />
                                Fit viewport
                                <FitViewportHelp text={FIT_VIEWPORT_HELP_TEXT} />
                            </Label>
                        </Flex>
                    </Flex>
                </Flex>
                {dialog.visible && (
                    <Box
                        sx={{
                            width: device === 'mobile' ? MOBILE_FRAME_WIDTH : '100%',
                            mx: device === 'mobile' ? 'auto' : 0,
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'gray.3',
                            borderRadius: 'sm',
                        }}
                    >
                        {/* Generic host-page mock: sticky header + filler content, so the
                            widget previews the way it will actually sit on a real page. */}
                        <Flex
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                alignItems: 'center',
                                gap: 3,
                                px: 3,
                                height: device === 'desktop' ? '64px' : '56px',
                                backgroundColor: 'background',
                                borderBottom: '1px solid',
                                borderColor: 'gray.3',
                            }}
                        >
                            <Text sx={{ fontWeight: 'bold' }}>Your Company</Text>
                            <Flex sx={{ gap: 3, ml: 'auto', fontSize: 0, color: 'gray.6' }}>
                                <Text>Home</Text>
                                <Text>Services</Text>
                                <Text>Contact</Text>
                            </Flex>
                        </Flex>
                        <Box sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '220px',
                                    mb: 4,
                                    backgroundColor: 'gray.2',
                                    border: '1px dashed',
                                    borderColor: 'gray.4',
                                    borderRadius: 'sm',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'gray.5',
                                    fontSize: 1,
                                }}
                            >
                                Image placeholder
                            </Box>
                            <Text sx={{ display: 'block', color: 'gray.6', fontSize: 1, mb: 3 }}>
                                Page content above the embedded widget goes here. Welcome to our salon — we&apos;ve
                                been serving the neighborhood for over a decade, offering a full range of hair care
                                services from quick trims to complete color transformations. Our stylists take the
                                time to understand exactly what you&apos;re looking for before ever picking up a pair
                                of scissors.
                            </Text>
                            <Text sx={{ display: 'block', color: 'gray.6', fontSize: 1, mb: 4 }}>
                                Browse our most popular services below and book a time that works for you. Walk-ins
                                are welcome, but booking ahead guarantees your preferred stylist and time slot.
                            </Text>
                            <Box key={`${scheme}-${fitViewport}`} ref={mountWidget} sx={{ minHeight: '640px' }} />
                            <Text sx={{ display: 'block', color: 'gray.6', fontSize: 1, mt: 4, mb: 3 }}>
                                Page content below the embedded widget goes here. Not sure which service is right
                                for you? Reach out to our team and we&apos;ll help you find the perfect fit, whether
                                it&apos;s a simple cut or a full color and styling package.
                            </Text>
                            <Text sx={{ display: 'block', color: 'gray.6', fontSize: 1 }}>
                                We also offer gift cards, loyalty rewards, and seasonal promotions — sign up for our
                                newsletter below to stay in the loop.
                            </Text>
                        </Box>
                        <Box
                            sx={{
                                px: 3,
                                py: 4,
                                backgroundColor: 'gray.1',
                                borderTop: '1px solid',
                                borderColor: 'gray.3',
                            }}
                        >
                            <Text sx={{ display: 'block', fontWeight: 'bold', mb: 2 }}>Your Company</Text>
                            <Flex sx={{ gap: 3, flexWrap: 'wrap', fontSize: 0, color: 'gray.6' }}>
                                <Text>About</Text>
                                <Text>Contact</Text>
                                <Text>Privacy policy</Text>
                            </Flex>
                            <Text sx={{ display: 'block', color: 'gray.5', fontSize: 0, mt: 3 }}>
                                © {new Date().getFullYear()} Your Company. All rights reserved.
                            </Text>
                        </Box>
                    </Box>
                )}
            </Dialog>
        </DialogBackdrop>
    );
};

export default WidgetPreviewDialog;
